/**
 * Seasonal Hook for v2v2 SWFL Farmers Market Application
 * React hook for accessing seasonal produce data with automatic updates
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  SeasonalData, 
  SeasonalProduce, 
  MonthAvailability,
  Month, 
  Season,
  AvailabilityStatus
} from '@/types/seasonal';
import {
  getSeasonalData,
  getCurrentMonth,
  getCurrentSeason,
  getSeasonFromMonth,
  getSeasonLabel,
  getAvailabilityForMonth,
  getProduceAvailabilityStatus,
  isInSeason,
  isYearRound,
  getProduceByCategory,
  getAllCategories,
  getAllProduce,
  getPeakProduce,
  getProduceByMonth,
  getUPickLocations,
  getAllUPickFarms,
  getAllFarmersMarkets,
  getShoppingTips,
  formatCurrentMonthData,
  getSeasonalHighlights,
  findProduceByName as findProduceByNameInData,
  searchProduceByKeyword,
  getCurrentlyPeakSeasonProduce,
  getCategoryDisplayName,
  getMonthAbbreviation,
  getAllMonths,
  clearSeasonalDataCache
} from '@/lib/seasonal-data';

interface UseSeasonalReturn {
  // Data
  data: SeasonalData | null;
  isLoading: boolean;
  error: string | null;
  
  // Current time
  currentMonth: Month;
  currentSeason: Season;
  
  // Availability
  currentAvailability: MonthAvailability | undefined;
  getAvailability: (month: Month) => MonthAvailability | undefined;
  
  // Produce queries
  peakProduce: string[];
  availableProduce: string[];
  limitedProduce: string[];
  allProduce: SeasonalProduce[];
  
  // Category queries
  categories: string[];
  getProduceByCategory: (category: string) => SeasonalProduce[];
  
  // Status queries
  isInSeason: (produceName: string) => boolean;
  isYearRound: (produceName: string) => boolean;
  getProduceStatus: (produceName: string, month?: Month) => AvailabilityStatus;
  
  // U-Pick locations
  uPickLocations: (produceName: string) => string[];
  allUPickFarms: string[];
  allFarmersMarkets: string[];
  
  // Shopping tips
  shoppingTips: Array<{ tip: string; description: string }>;
  
  // Formatted data
  currentMonthData: ReturnType<typeof formatCurrentMonthData>;
  seasonalHighlights: string[];
  
  // Search
  findProduce: (name: string) => SeasonalProduce | undefined;
  searchProduce: (keyword: string) => SeasonalProduce[];
  getPeakSeasonProduce: () => SeasonalProduce[];
  
  // Utilities
  getSeasonLabel: (season: Season) => string;
  getCategoryDisplayName: (category: string) => string;
  getMonthAbbreviation: (month: string) => string;
  getAllMonths: () => Month[];
  getProduceByMonth: (month: Month, status: 'peak' | 'available' | 'limited') => string[];
  
  // Refresh
  refresh: () => Promise<void>;
}

export function useSeasonal(): UseSeasonalReturn {
  const [data, setData] = useState<SeasonalData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load data
  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const seasonalData = await getSeasonalData();
        if (mounted) {
          setData(seasonalData);
        }
      } catch (err) {
        if (mounted) {
          setError('Failed to load seasonal data');
          console.error(err);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, []);

  // Derived state
  const currentMonth = useMemo(() => getCurrentMonth(), []);
  const currentSeason = useMemo(() => getCurrentSeason(), []);
  
  const currentAvailability = useMemo(() => {
    if (!data) return undefined;
    return data.months[currentMonth];
  }, [data, currentMonth]);

  const peakProduce = useMemo(() => {
    if (!data) return [];
    return data.months[data.currentMonth]?.peak || [];
  }, [data]);

  const availableProduce = useMemo(() => {
    if (!data) return [];
    return data.months[data.currentMonth]?.available || [];
  }, [data]);

  const limitedProduce = useMemo(() => {
    if (!data) return [];
    return data.months[data.currentMonth]?.limited || [];
  }, [data]);

  const allProduce = useMemo(() => {
    if (!data) return [];
    return getAllProduce(data);
  }, [data]);

  const categories = useMemo(() => {
    if (!data) return [];
    return getAllCategories(data);
  }, [data]);

  const allUPickFarms = useMemo(() => {
    if (!data) return [];
    return getAllUPickFarms(data);
  }, [data]);

  const allFarmersMarkets = useMemo(() => {
    if (!data) return [];
    return getAllFarmersMarkets(data);
  }, [data]);

  const shoppingTips = useMemo(() => {
    if (!data) return [];
    return getShoppingTips(data);
  }, [data]);

  const currentMonthData = useMemo(() => {
    if (!data) {
      return {
        month: currentMonth,
        season: currentSeason,
        seasonLabel: getSeasonLabel(currentSeason),
        peakProduce: [] as string[],
        availableProduce: [] as string[],
        limitedProduce: [] as string[],
        highlights: '',
        tips: '',
        totalPeak: 0,
        totalAvailable: 0,
      };
    }
    return formatCurrentMonthData(data);
  }, [data, currentMonth, currentSeason]);

  const seasonalHighlights = useMemo(() => {
    if (!data) return [];
    return getSeasonalHighlights(5, data);
  }, [data]);

  // Helper functions
  const getAvailability = useCallback((month: Month) => {
    if (!data) return undefined;
    return getAvailabilityForMonth(month, data);
  }, [data]);

  const getCategoryProduce = useCallback((category: string) => {
    if (!data) return [];
    return getProduceByCategory(category, data);
  }, [data]);

  const checkIsInSeason = useCallback((produceName: string) => {
    if (!data) return false;
    return isInSeason(produceName, data);
  }, [data]);

  const checkIsYearRound = useCallback((produceName: string) => {
    if (!data) return false;
    return isYearRound(produceName, data);
  }, [data]);

  const getStatus = useCallback((produceName: string, month?: Month) => {
    if (!data) return 'limited';
    const targetMonth = month || currentMonth;
    return getProduceAvailabilityStatus(produceName, targetMonth, data);
  }, [data, currentMonth]);

  const getUPickForProduce = useCallback((produceName: string) => {
    if (!data) return [];
    return getUPickLocations(produceName, data);
  }, [data]);

  const findProduceByName = useCallback((name: string) => {
    if (!data) return undefined;
    return findProduceByNameInData(name, data);
  }, [data]);

  const searchByKeyword = useCallback((keyword: string) => {
    if (!data) return [];
    return searchProduceByKeyword(keyword, data);
  }, [data]);

  const getPeakSeasonProduceItems = useCallback(() => {
    if (!data) return [];
    return getCurrentlyPeakSeasonProduce(data);
  }, [data]);

  const refresh = useCallback(async () => {
    clearSeasonalDataCache();
    setIsLoading(true);
    try {
      const seasonalData = await getSeasonalData();
      setData(seasonalData);
      setError(null);
    } catch (err) {
      setError('Failed to refresh seasonal data');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    data,
    isLoading,
    error,
    currentMonth,
    currentSeason,
    currentAvailability,
    getAvailability,
    peakProduce,
    availableProduce,
    limitedProduce,
    allProduce,
    categories,
    getProduceByCategory: getCategoryProduce,
    isInSeason: checkIsInSeason,
    isYearRound: checkIsYearRound,
    getProduceStatus: getStatus,
    uPickLocations: getUPickForProduce,
    allUPickFarms,
    allFarmersMarkets,
    shoppingTips,
    currentMonthData,
    seasonalHighlights,
    findProduce: findProduceByName,
    searchProduce: searchByKeyword,
    getPeakSeasonProduce: getPeakSeasonProduceItems,
    getSeasonLabel,
    getCategoryDisplayName,
    getMonthAbbreviation,
    getAllMonths,
    getProduceByMonth,
    refresh,
  };
}

/**
 * Hook for getting produce availability for a specific month
 */
export function useMonthlyAvailability(month: Month) {
  const { getAvailability, isLoading, error } = useSeasonal();
  const availability = getAvailability(month);

  return {
    availability,
    isLoading,
    error,
    isPeak: (produce: string) => availability?.peak.includes(produce) || false,
    isAvailable: (produce: string) => 
      availability?.peak.includes(produce) || availability?.available.includes(produce) || false,
    isLimited: (produce: string) => availability?.limited.includes(produce) || false,
  };
}

/**
 * Hook for getting produce status with current month as default
 */
export function useProduceStatus(produceName: string) {
  const { getProduceStatus, isLoading, error } = useSeasonal();
  const status = getProduceStatus(produceName);

  return {
    status,
    isLoading,
    error,
    isPeak: status === 'peak',
    isAvailable: status === 'available',
    isLimited: status === 'limited',
    isYearRound: status === 'year-round',
  };
}

/**
 * Hook for getting U-Pick locations for a produce
 */
export function useUPickLocations(produceName: string) {
  const { uPickLocations, allUPickFarms, isLoading, error } = useSeasonal();
  const locations = uPickLocations(produceName);

  return {
    locations,
    allUPickFarms,
    isLoading,
    error,
    hasUPick: locations.length > 0,
  };
}
